import React, { createContext, useContext, useEffect, useState } from 'react';
import commonApi from '../utils/api';
import { CommonContext } from './CommonContext';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

export const MasterDataContext = createContext();

const initialFilters = {
    searchBy: 'awb_no',
    searchValue: '',
    merchant_id: [],
    courier_id: [],
    delivery_type_id: '',
    booking_user_id: [],
    cs_user_id: [],
    order_status_id: [],
    dateRange: {
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
        endDate: new Date()
    },
};

export const MasterDataContextProvider = ({ children }) => {
    const { user, setAlertMessage, setOpenAlert, setSeverity } = useContext(CommonContext);
    const navigate = useNavigate();
    const [filters, setFilters] = useState(initialFilters);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [allOrders, setAllOrders] = useState([]);
    const [allData, setAllData] = useState([]);
    const [firstLoading, setFirstLoading] = useState(false);

    const onLoadApi = (customFilters = filters) => {
        const payload = {
            ...customFilters,
            start_date: customFilters?.dateRange?.startDate
                ? format(new Date(customFilters.dateRange.startDate), 'yyyy-MM-dd')
                : null,
            end_date: customFilters?.dateRange?.endDate
                ? format(new Date(customFilters.dateRange.endDate), 'yyyy-MM-dd')
                : null,
        };

        if (user?.user_type !== "Merchant") {
            payload.admin_id = user?.id;
        } else {
            payload.merchant_id = [user?.id];
        }

        delete payload.dateRange;
        setAllOrders([]);
        setAllData([]);
        setFirstLoading(false);
        setIsLoading(true);

        commonApi.post(`/orders/list?page=${page + 1}&per_page=${rowsPerPage}`, payload)
            .then((response) => {
                setIsLoading(false);
                setFirstLoading(true);
                if (response?.data?.data) {
                    setAllOrders(response?.data?.data);
                    setAllData(response?.data);
                }
            })
            .catch((error) => {
                setIsLoading(false);
                setFirstLoading(true);
                console.error('Error fetching data:', error);
            });
    };

    const onExport = () => {
        const isOtherFilterActive =
            filters?.searchValue ||
            (filters?.merchant_id && filters.merchant_id.length > 0) ||
            (filters?.courier_id && filters.courier_id.length > 0) ||
            filters?.delivery_type_id ||
            (filters?.booking_user_id && filters.booking_user_id.length > 0) ||
            (filters?.cs_user_id && filters.cs_user_id.length > 0) ||
            (filters?.order_status_id && filters.order_status_id.length > 0);

        if (!isOtherFilterActive && (!filters?.dateRange?.startDate || !filters?.dateRange?.endDate)) {
            setAlertMessage('Date range is mandatory for export');
            setSeverity('error');
            setOpenAlert(true);
            return;
        }

        const payload = {
            ...filters,
            start_date: filters?.dateRange?.startDate ? format(new Date(filters.dateRange.startDate), 'yyyy-MM-dd') : null,
            end_date: filters?.dateRange?.endDate ? format(new Date(filters.dateRange.endDate), 'yyyy-MM-dd') : null,
        };

        if (user?.user_type !== "Merchant") {
            payload.admin_id = user?.id;
        } else {
            payload.merchant_id = [user?.id];
        }
        delete payload.dateRange;

        setIsLoading(true);
        commonApi.post('/orders/export-master-data', payload)
            .then((response) => {
                setIsLoading(false);
                setOpenAlert(true);
                if (response?.data?.s === 0) {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error');
                } else {
                    setAlertMessage(response?.data?.msg || 'Export started. Check downloads.');
                    setSeverity('success');
                    navigate('/download');
                }
            })
            .catch((error) => {
                setIsLoading(false);
                setOpenAlert(true);
                setAlertMessage(error?.response?.data?.msg || error?.message || 'Something went wrong during export');
                setSeverity('error');
            });
    };

    useEffect(() => {
        onLoadApi();
    }, [page, rowsPerPage]);

    const contextValue = {
        filters,
        setFilters,
        initialFilters,
        isLoading,
        setIsLoading,
        onLoadApi,
        onExport,
        allOrders,
        allData,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        firstLoading,
    };

    return (
        <MasterDataContext.Provider value={contextValue}>
            {children}
        </MasterDataContext.Provider>
    );
};
